import React, { useEffect, useState } from 'react';
import Swal, { SweetAlertOptions } from 'sweetalert2';
import { differenceInDays, endOfMonth, endOfWeek, endOfYear, format, isAfter, isBefore, parseISO, startOfMonth, startOfWeek, startOfYear, subDays, subMonths, subYears } from 'date-fns';
import { Link } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { UseOutletContext } from './Main';
import { StateLanguage } from '../../languages/config/StateLanguage';
import { DashboardDA } from '../../services/dashboard.service';
import { Error } from '../../models/error.model';
import { CompanyDashboardInsight } from '../../models/company.dashboard.insight.model';
import { CompanyDashboardComparsion } from '../../models/company.dashboard.comparsion.model';
import { CompanyDashboardAnalysis } from '../../models/company.dashboard.analysis.model';
import { DashboardInsight } from '../../models/dashboard.insight.model';
import { DashboardComparsion } from '../../models/dashboard.comparsion.model';
import { DashboardAnalysis } from '../../models/dashboard.analysis.model';
import { Container } from '../../styles/container.style';
import { imageCompany } from '../../scripts/image.value.script';
import { contactFormat, moneyFormat, percentageFormat } from '../../scripts/format.value.script';
import { dateFormatAbbreviated } from '../../scripts/datetime.format.script';
import { colorPercentageDebt, colorValidUntilQuote } from '../../scripts/badge.color.script';
import { countryName } from '../../libraries/country.library';
import { percentageCalculate } from '../../scripts/computed.amount.script';
import { getError } from '../../languages/translations/response';

import AppPreloader from '../../components/Preloader';
import AppChartGaugeQuote from '../../components/chart/ChartGaugeQuotes';
import AppChartBarInvoices from '../../components/chart/ChartBarInvoices';
import AppChartBarSalesPurchases from '../../components/chart/ChartBarSalesPurchases';
import AppChartGaugeOrder from '../../components/chart/ChartGaugeOrders';

export interface DashboardPageProps {};

let errorResponse: Error, dashboardInsightResponse: CompanyDashboardInsight, dashboardComparsionResponse: CompanyDashboardComparsion, dashboardAnalysisResponse: CompanyDashboardAnalysis;

const DashboardPage: React.FunctionComponent<DashboardPageProps> = props => {
  const {setRoute, companyForUser} = UseOutletContext()
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [customOptions, setCustomOptions] = useState(false)
  const [cardValue, setCardValue] = useState({expenses: '', turnout: '', amount_orders_to_billed: '', amount_debt: '', amount_paid: '', quotes_approved: ''})
  const [timeInterval, setTimeInterval] = useState({range: '', current: {start_date:'', end_date: ''}, compare: {start_date:'', end_date: ''}})
  const [range, setRange] = useState({list: [] as string [], current: ''})
  const [topCustomer, setTopCustomer] = useState('')
  const [customCurrentStartDate, setCustomCurrentStartDate] = useState('')
  const [customCurrentEndDate, setCustomCurrentEndDate] = useState('')
  const [customCompareStartDate, setCustomCompareStartDate] = useState('')
  const [customCompareEndDate, setCustomCompareEndDate] = useState('')
  const [dashboardInsight, setDashboardInsight] = useState<DashboardInsight | null>(null)
  const [dashboardComparsion, setDashboardComparsion] = useState<DashboardComparsion | null>(null)
  const [dashboardAnalysis, setDashboardAnalysis] = useState<DashboardAnalysis | null>(null)

  const loadDashboardInsight = async (id_company: string) => {
    await DashboardDA.getDashboardInsight(id_company, timeInterval.range, timeInterval.current.start_date, timeInterval.current.end_date, timeInterval.compare.start_date, timeInterval.compare.end_date).then( (response) => {
      if (response.status === 200) {
        dashboardInsightResponse = response.data

        setDashboardInsight(dashboardInsightResponse.data)
        setTimeInterval({range: dashboardInsightResponse.time_interval.range, current: {start_date: dashboardInsightResponse.time_interval.current.start_date, end_date: dashboardInsightResponse.time_interval.current.end_date}, compare: {start_date: dashboardInsightResponse.time_interval.compare.start_date, end_date: dashboardInsightResponse.time_interval.compare.end_date}})
      } else {
        errorResponse = response.data

        Swal.fire({
          title: getError(errorResponse.code, lang.code),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          buttonsStyling: !1,
          confirmButtonText: lang.labels.okGotIt,
          customClass: {confirmButton: 'btn btn-primary'}
        } as SweetAlertOptions)
      }
    }).catch( (error) => {
      console.error(error)
      navigate('/error')
    })
  }

  const loadDashboardComparsion = async (id_company: string) => {
    await DashboardDA.getDashboardComparsion(id_company, range.current).then( (response) => {
      if (response.status === 200) {
        dashboardComparsionResponse = response.data

        setDashboardComparsion(dashboardComparsionResponse.data)
        setRange({list: dashboardComparsionResponse.range.list, current: range.current})
      } else {
        errorResponse = response.data

        Swal.fire({
          title: getError(errorResponse.code, lang.code),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          buttonsStyling: !1,
          confirmButtonText: lang.labels.okGotIt,
          customClass: {confirmButton: 'btn btn-primary'}
        } as SweetAlertOptions)
      }
    }).catch( (error) => {
      console.error(error)
      navigate('/error')
    })
  }

  const loadDashboardAnalysis = async (id_company: string) => {
    await DashboardDA.getDashboardAnalysis(id_company).then( (response) => {
      if (response.status === 200) {
        dashboardAnalysisResponse = response.data

        setDashboardAnalysis(dashboardAnalysisResponse.data)
      } else {
        errorResponse = response.data

        Swal.fire({
          title: getError(errorResponse.code, lang.code),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          buttonsStyling: !1,
          confirmButtonText: lang.labels.okGotIt,
          customClass: {confirmButton: 'btn btn-primary'}
        } as SweetAlertOptions)
      }
    }).catch( (error) => {
      console.error(error)
      navigate('/error')
    })
  }

  const handleChangeCurrentStartDate = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
    let currentStartDate: string = event.target.value.trim() || timeInterval.current.start_date.substring(0, 10);
    let parsedCurrentStart: Date = parseISO(currentStartDate);
    let parsedCurrentEnd: Date = parseISO(customCurrentEndDate);
    let parsedCompareEnd: Date = parseISO(customCompareEndDate);

    if (isBefore(parsedCurrentStart, parsedCompareEnd)) {
      let rangeTimeInterval: number = differenceInDays(parsedCurrentEnd, parsedCurrentStart);
      let dateCompareStart: Date = subDays(parsedCurrentStart, rangeTimeInterval + 1);
      let dateCompareEnd: Date = subDays(parsedCurrentStart, 1);
      setCustomCompareStartDate(format(dateCompareStart, 'yyyy-MM-dd'));
      setCustomCompareEndDate(format(dateCompareEnd, 'yyyy-MM-dd'));
    }

    if (isAfter(parsedCurrentStart, parsedCurrentEnd)) {
      currentStartDate = format(parsedCurrentEnd, 'yyyy-MM-dd');
    }

    setCustomCurrentStartDate(currentStartDate);
  }

  const handleChangeCurrentEndDate = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
    let currentEndDate: string = event.target.value.trim() || timeInterval.current.end_date.substring(0, 10)
    setCustomCurrentEndDate(currentEndDate)
  }

  const handleChangeCompareStartDate = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
    let compareStartDate: string = event.target.value.trim() || timeInterval.compare.start_date.substring(0, 10)
    let parsedCompareStartDate: Date = parseISO(compareStartDate)
    let parsedCompareEndDate: Date = parseISO(customCompareEndDate)

    if (isAfter(parsedCompareStartDate, parsedCompareEndDate)) {
      compareStartDate = format(parsedCompareEndDate, 'yyyy-MM-dd')
    } else {
      compareStartDate = format(parsedCompareStartDate, 'yyyy-MM-dd')
    }

    setCustomCompareStartDate(compareStartDate)
  }

  const handleChangeCompareEndDate = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let compareEndDate: string = event.target.value.trim() || timeInterval.compare.end_date.substring(0, 10)
    let parsedCurrentStartDate: Date = parseISO(customCurrentStartDate)
    let parsedCurrentEndDate: Date = parseISO(customCurrentEndDate)
    let parsedCompareEndDate: Date = parseISO(compareEndDate)
  
    if (isAfter(parsedCurrentStartDate, parsedCompareEndDate)) {
      let rangeTimeInterval: number = differenceInDays(parsedCurrentEndDate, parsedCurrentStartDate)
      let dateCompareStartDate: Date = subDays(parsedCurrentStartDate, rangeTimeInterval + 1)
      compareEndDate = format(subDays(parsedCurrentStartDate, 1), 'yyyy-MM-dd')
      setCustomCompareStartDate(format(dateCompareStartDate, 'yyyy-MM-dd'))
    }

    setCustomCompareEndDate(compareEndDate)
  }

  const executeChangeCardValue = (item: string, sub_item: string) => {
    if (cardValue[item as keyof typeof cardValue] !== sub_item) {
      setCardValue(cardValue => ({ ...cardValue, [item]: sub_item }))
    }
  }

  const executeChangeTopCustomer = (item: string) => {
    if (topCustomer !== item) {
      setTopCustomer(item)
    }
  }

  const executeSelectTimeIntervalRange = (item: string) => {
    if (item === 'custom') {
      setCustomCurrentStartDate(timeInterval.current.start_date.substring(0, 10))
      setCustomCurrentEndDate(timeInterval.current.end_date.substring(0, 10))
      setCustomCompareStartDate(timeInterval.compare.start_date.substring(0, 10))
      setCustomCompareEndDate(timeInterval.compare.end_date.substring(0, 10))
      setCustomOptions(true)
    } else {
      if (companyForUser && timeInterval.range !== item) {
        let dateCurrent: Date = new Date()
        let currentStartDate: Date = new Date()
        let currentEndDate: Date = new Date()
        let compareStartDate: Date = new Date()
        let compareEndDate: Date = new Date()

        switch (item) {
          case 'year':
            currentStartDate = startOfYear(dateCurrent)
            compareStartDate = startOfYear(subYears(dateCurrent, 1))
            compareEndDate = endOfYear(subYears(dateCurrent, 1))
            break;
          case 'month':
            currentStartDate = startOfMonth(dateCurrent)
            compareStartDate = startOfMonth(subMonths(dateCurrent, 1))
            compareEndDate = endOfMonth(subMonths(dateCurrent, 1))
            break;
          case 'week':
            currentStartDate = startOfWeek(dateCurrent)
            compareStartDate = startOfWeek(subDays(dateCurrent, 7))
            compareEndDate = endOfWeek(subDays(dateCurrent, 7))
            break;
          case 'yesterday':
            currentStartDate = subDays(dateCurrent, 1)
            currentEndDate = subDays(dateCurrent, 1)
            compareStartDate = subDays(dateCurrent, 2)
            compareEndDate = subDays(dateCurrent, 2)
            break;
          case 'today':
            compareStartDate = subDays(dateCurrent, 1)
            compareEndDate = subDays(dateCurrent, 1)
            break;
        }

        timeInterval.current.start_date = format(currentStartDate, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
        timeInterval.current.end_date = format(currentEndDate, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
        timeInterval.compare.start_date = format(compareStartDate, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
        timeInterval.compare.end_date = format(compareEndDate, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
        timeInterval.range = item

        loadDashboardInsight(companyForUser.id)
      }
    }
  }

  const executeSelectYearIntervalRange = (item: string) => {
    if (companyForUser && range.current !== item) {
      range.current = item
      loadDashboardComparsion(companyForUser.id)
    }
  }

  const executeCustomRangeApply = () => {
    if (companyForUser) {
      timeInterval.range = 'custom'
      timeInterval.current.start_date = format((new Date(`${customCurrentStartDate}T00:00:00.000`)), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
      timeInterval.current.end_date = format((new Date(`${customCurrentEndDate}T23:59:59.999`)), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
      timeInterval.compare.start_date = format((new Date(`${customCompareStartDate}T00:00:00.000`)), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
      timeInterval.compare.end_date = format((new Date(`${customCompareEndDate}T23:59:59.999`)), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
      loadDashboardInsight(companyForUser.id)
    }
  }

  const executeCustomRangeCancel = () => {
    setCustomOptions(false)
  }

  function maxTimeIntervalCurrent(): string {
    let date: Date = new Date()
    return format(date, 'yyyy-MM-dd')
  }

  function maxTimeIntervalCompare(): string {
    let date: Date = (customCurrentStartDate) ? subDays(parseISO(customCurrentStartDate), 1) : new Date()
    return format(date, 'yyyy-MM-dd')
  }

  useEffect( () => {
    if (companyForUser) {
      let dateCurrent: Date = new Date()
      let yearCurrent: Date = startOfYear(dateCurrent)
      let yearCompare: Date = startOfYear(subYears(dateCurrent, 1))

      timeInterval.range = 'year'
      timeInterval.current.start_date = format(yearCurrent, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`);
      timeInterval.current.end_date = format(dateCurrent, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`);
      timeInterval.compare.start_date = format(yearCompare, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`);
      timeInterval.compare.end_date = format(endOfYear(subYears(dateCurrent, 1)), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`);
      range.current = format(dateCurrent, 'yyyy');

      setDashboardInsight(null)
      setDashboardComparsion(null)
      setDashboardAnalysis(null)
      setCardValue({expenses: 'abbreviation', turnout: 'abbreviation', amount_orders_to_billed: 'abbreviation', amount_debt: 'abbreviation', amount_paid: 'abbreviation', quotes_approved: 'abbreviation'})
      setTopCustomer('best')

      loadDashboardInsight(companyForUser.id)
      loadDashboardComparsion(companyForUser.id)
      loadDashboardAnalysis(companyForUser.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: null, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && dashboardInsight && dashboardComparsion && dashboardAnalysis
        ?
        <>
          <div className="d-flex flex-wrap flex-stack mb-5 px-2">
            <h3 className="fw-bold">
              {lang.labels.companyInsight}
              <span className="fs-6 text-gray-400 fw-semibold ms-1">{lang.labels.byTimeRange}</span>
            </h3>
            <div className="d-flex flex-wrap align-items-center">
              <div className="ranges me-5">
                <Dropdown>
                  <Dropdown.Toggle variant="btn btn-sm btn-light bg-body border border-dark d-flex align-items-center justify-content-between py-1 ps-3 pe-1 w-225px">
                    <div className="text-gray-900 fw-bold">{dateFormatAbbreviated(lang.code, timeInterval.current.start_date)} - {dateFormatAbbreviated(lang.code, timeInterval.current.end_date)}</div>
                    <span className="svg-icon svg-icon-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M11.4343 12.7344L7.25 8.55005C6.83579 8.13583 6.16421 8.13584 5.75 8.55005C5.33579 8.96426 5.33579 9.63583 5.75 10.05L11.2929 15.5929C11.6834 15.9835 12.3166 15.9835 12.7071 15.5929L18.25 10.05C18.6642 9.63584 18.6642 8.96426 18.25 8.55005C17.8358 8.13584 17.1642 8.13584 16.75 8.55005L12.5657 12.7344C12.2533 13.0468 11.7467 13.0468 11.4343 12.7344Z" fill="black"></path>
                      </svg>
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu variant="daterangepicker ltr show-ranges opensleft" align="end">
                    <Container property={(!customOptions).toString()}>
                      <div className="ranges w-250px">
                        <Dropdown.Item active={timeInterval.range === "year"} onClick={() => executeSelectTimeIntervalRange("year")}>
                          <p className="px-3 py-1 fw-bold m-0">{lang.labels.thisYear}</p>
                        </Dropdown.Item>
                        <Dropdown.Item active={timeInterval.range === "month"} onClick={() => executeSelectTimeIntervalRange("month")}>
                          <p className="px-3 py-1 fw-bold m-0">{lang.labels.thisMonth}</p>
                        </Dropdown.Item>
                        <Dropdown.Item active={timeInterval.range === "week"} onClick={() => executeSelectTimeIntervalRange("week")}>
                          <p className="px-3 py-1 fw-bold m-0">{lang.labels.thisWeek}</p>
                        </Dropdown.Item>
                        <Dropdown.Item active={timeInterval.range === "yesterday"} onClick={() => executeSelectTimeIntervalRange("yesterday")}>
                          <p className="px-3 py-1 fw-bold m-0">{lang.labels.yesterday}</p>
                        </Dropdown.Item>
                        <Dropdown.Item active={timeInterval.range === "today"} onClick={() => executeSelectTimeIntervalRange("today")}>
                          <p className="px-3 py-1 fw-bold m-0">{lang.labels.today}</p>
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <li className={`btn rounded-0 w-100 px-3 py-1 ${timeInterval.range === "custom" ? "bg-primary" : "btn-light bg-body"}`} onClick={() => executeSelectTimeIntervalRange("custom")}>
                          <p className={`px-3 py-1 m-0 fs-7 text-start fw-bold ${timeInterval.range === "custom" ? "text-white" : "text-dark"}`}>{lang.labels.customRange}</p>
                        </li>
                      </div>
                    </Container>
                    <Container property={(customOptions).toString()}>
                      <div className="ranges w-350px">
                        <div className="mx-5 my-3">
                          <label className="form-label fw-bolder">{lang.labels.timeInterval}:</label>
                          <div className="row">
                            <div className="col-md-6">
                              <label className="col-5 col-md-12 form-label fs-7">{lang.labels.startDate}:</label>
                              <input className="col-7 col-md-12 small-input rounded p-1 my-1 my-md-0" type="date" name="time-interval-current-start-date" max={customCurrentEndDate} value={customCurrentStartDate} onChange={handleChangeCurrentStartDate} />
                            </div>
                            <div className="col-md-6">
                              <label className="col-5 col-md-12 form-label fs-7">{lang.labels.endDate}:</label>
                              <input className="col-7 col-md-12 small-input rounded p-1 my-1 my-md-0" type="date" name="time-interval-current-end-date" min={customCurrentStartDate} max={maxTimeIntervalCurrent()} value={customCurrentEndDate} onChange={handleChangeCurrentEndDate} />
                            </div>
                          </div>
                        </div>
                        <div className="mx-5 mb-3">
                          <label className="form-label fw-bolder">{lang.labels.comparativeInterval}:</label>
                          <div className="row">
                            <div className="col-md-6">
                              <label className="col-5 col-md-12 form-label fs-7">{lang.labels.startDate}:</label>
                              <input className="col-7 col-md-12 small-input rounded p-1 my-1 my-md-0" type="date" name="time-interval-compare-start-date" max={customCompareEndDate} value={customCompareStartDate} onChange={handleChangeCompareStartDate} />
                            </div>
                            <div className="col-md-6">
                              <label className="col-5 col-md-12 form-label fs-7">{lang.labels.endDate}:</label>
                              <input className="col-7 col-md-12 small-input rounded p-1 my-1 my-md-0" type="date" name="time-interval-compare-end-date" min={customCompareStartDate} max={maxTimeIntervalCompare()} value={customCompareEndDate} onChange={handleChangeCompareEndDate} />
                            </div>
                          </div>
                        </div>
                        <div className="separator mb-2"></div>
                        <div className="drp-buttons d-flex justify-content-end mx-7 mb-1">
                          <button className="cancelBtn btn btn-sm btn-light" type="button" onClick={executeCustomRangeCancel}>{lang.labels.cancel}</button>
                          <Dropdown.Item bsPrefix='applyBtn btn btn-sm btn-primary ms-3' onClick={executeCustomRangeApply}>{lang.labels.apply}</Dropdown.Item>
                        </div>
                      </div>
                    </Container>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
              <div className="btn btn-sm btn-icon btn-light btn-color-muted btn-active-primary active">
                <span className="svg-icon svg-icon-1">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path opacity="0.3" d="M21 22H3C2.4 22 2 21.6 2 21V5C2 4.4 2.4 4 3 4H21C21.6 4 22 4.4 22 5V21C22 21.6 21.6 22 21 22Z" fill="currentColor"></path>
                    <path d="M6 6C5.4 6 5 5.6 5 5V3C5 2.4 5.4 2 6 2C6.6 2 7 2.4 7 3V5C7 5.6 6.6 6 6 6ZM11 5V3C11 2.4 10.6 2 10 2C9.4 2 9 2.4 9 3V5C9 5.6 9.4 6 10 6C10.6 6 11 5.6 11 5ZM15 5V3C15 2.4 14.6 2 14 2C13.4 2 13 2.4 13 3V5C13 5.6 13.4 6 14 6C14.6 6 15 5.6 15 5ZM19 5V3C19 2.4 18.6 2 18 2C17.4 2 17 2.4 17 3V5C17 5.6 17.4 6 18 6C18.6 6 19 5.6 19 5Z" fill="currentColor"></path>
                    <path d="M8.8 13.1C9.2 13.1 9.5 13 9.7 12.8C9.9 12.6 10.1 12.3 10.1 11.9C10.1 11.6 10 11.3 9.8 11.1C9.6 10.9 9.3 10.8 9 10.8C8.8 10.8 8.59999 10.8 8.39999 10.9C8.19999 11 8.1 11.1 8 11.2C7.9 11.3 7.8 11.4 7.7 11.6C7.6 11.8 7.5 11.9 7.5 12.1C7.5 12.2 7.4 12.2 7.3 12.3C7.2 12.4 7.09999 12.4 6.89999 12.4C6.69999 12.4 6.6 12.3 6.5 12.2C6.4 12.1 6.3 11.9 6.3 11.7C6.3 11.5 6.4 11.3 6.5 11.1C6.6 10.9 6.8 10.7 7 10.5C7.2 10.3 7.49999 10.1 7.89999 10C8.29999 9.90003 8.60001 9.80003 9.10001 9.80003C9.50001 9.80003 9.80001 9.90003 10.1 10C10.4 10.1 10.7 10.3 10.9 10.4C11.1 10.5 11.3 10.8 11.4 11.1C11.5 11.4 11.6 11.6 11.6 11.9C11.6 12.3 11.5 12.6 11.3 12.9C11.1 13.2 10.9 13.5 10.6 13.7C10.9 13.9 11.2 14.1 11.4 14.3C11.6 14.5 11.8 14.7 11.9 15C12 15.3 12.1 15.5 12.1 15.8C12.1 16.2 12 16.5 11.9 16.8C11.8 17.1 11.5 17.4 11.3 17.7C11.1 18 10.7 18.2 10.3 18.3C9.9 18.4 9.5 18.5 9 18.5C8.5 18.5 8.1 18.4 7.7 18.2C7.3 18 7 17.8 6.8 17.6C6.6 17.4 6.4 17.1 6.3 16.8C6.2 16.5 6.10001 16.3 6.10001 16.1C6.10001 15.9 6.2 15.7 6.3 15.6C6.4 15.5 6.6 15.4 6.8 15.4C6.9 15.4 7.00001 15.4 7.10001 15.5C7.20001 15.6 7.3 15.6 7.3 15.7C7.5 16.2 7.7 16.6 8 16.9C8.3 17.2 8.6 17.3 9 17.3C9.2 17.3 9.5 17.2 9.7 17.1C9.9 17 10.1 16.8 10.3 16.6C10.5 16.4 10.5 16.1 10.5 15.8C10.5 15.3 10.4 15 10.1 14.7C9.80001 14.4 9.50001 14.3 9.10001 14.3C9.00001 14.3 8.9 14.3 8.7 14.3C8.5 14.3 8.39999 14.3 8.39999 14.3C8.19999 14.3 7.99999 14.2 7.89999 14.1C7.79999 14 7.7 13.8 7.7 13.7C7.7 13.5 7.79999 13.4 7.89999 13.2C7.99999 13 8.2 13 8.5 13H8.8V13.1ZM15.3 17.5V12.2C14.3 13 13.6 13.3 13.3 13.3C13.1 13.3 13 13.2 12.9 13.1C12.8 13 12.7 12.8 12.7 12.6C12.7 12.4 12.8 12.3 12.9 12.2C13 12.1 13.2 12 13.6 11.8C14.1 11.6 14.5 11.3 14.7 11.1C14.9 10.9 15.2 10.6 15.5 10.3C15.8 10 15.9 9.80003 15.9 9.70003C15.9 9.60003 16.1 9.60004 16.3 9.60004C16.5 9.60004 16.7 9.70003 16.8 9.80003C16.9 9.90003 17 10.2 17 10.5V17.2C17 18 16.7 18.4 16.2 18.4C16 18.4 15.8 18.3 15.6 18.2C15.4 18.1 15.3 17.8 15.3 17.5Z" fill="currentColor"></path>
                  </svg>
                </span>
              </div>
            </div>
          </div>
          <div className="row g-2 g-xl-3 mb-5">
            <div className="col-sm-6 col-xl-2">
              <div className="card h-lg-100">
                <div className="card-body d-flex justify-content-between align-items-start flex-column p-4">
                  <div className="d-flex justify-content-between align-items-start w-100">
                    <span className="svg-icon svg-icon-2hx svg-icon-gray-500">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path opacity="0.3" d="M21.25 18.525L13.05 21.825C12.35 22.125 11.65 22.125 10.95 21.825L2.75 18.525C1.75 18.125 1.75 16.725 2.75 16.325L4.04999 15.825L10.25 18.325C10.85 18.525 11.45 18.625 12.05 18.625C12.65 18.625 13.25 18.525 13.85 18.325L20.05 15.825L21.35 16.325C22.35 16.725 22.35 18.125 21.25 18.525ZM13.05 16.425L21.25 13.125C22.25 12.725 22.25 11.325 21.25 10.925L13.05 7.62502C12.35 7.32502 11.65 7.32502 10.95 7.62502L2.75 10.925C1.75 11.325 1.75 12.725 2.75 13.125L10.95 16.425C11.65 16.725 12.45 16.725 13.05 16.425Z" fill="currentColor"></path>
                        <path d="M11.05 11.025L2.84998 7.725C1.84998 7.325 1.84998 5.925 2.84998 5.525L11.05 2.225C11.75 1.925 12.45 1.925 13.15 2.225L21.35 5.525C22.35 5.925 22.35 7.325 21.35 7.725L13.05 11.025C12.45 11.325 11.65 11.325 11.05 11.025Z" fill="currentColor"></path>
                      </svg>
                    </span>
                    <Dropdown>
                      <Dropdown.Toggle variant="btn btn-icon btn-color-gray-500 btn-active-color-primary w-auto h-auto">
                        <span className="svg-icon svg-icon-1">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="4" fill="currentColor"></rect>
                            <rect x="11" y="11" width="2.6" height="2.6" rx="1.3" fill="currentColor"></rect>
                            <rect x="15" y="11" width="2.6" height="2.6" rx="1.3" fill="currentColor"></rect>
                            <rect x="7" y="11" width="2.6" height="2.6" rx="1.3" fill="currentColor"></rect>
                          </svg>
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-700 menu-state-bg-light-primary fw-bold fs-8 py-2" align="end">
                        <Dropdown.Item bsPrefix="menu-item px-2" onClick={() => executeChangeCardValue("expenses", "abbreviation")}>
                          <div className="menu-link fw-bold px-2 py-1 d-flex align-items-center justify-content-between">
                            {lang.labels.abbreviation}
                            { cardValue.expenses === "abbreviation" &&
                              <span className="svg-icon svg-icon-4 svg-icon-primary">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M9.89557 13.4982L7.79487 11.2651C7.26967 10.7068 6.38251 10.7068 5.85731 11.2651C5.37559 11.7772 5.37559 12.5757 5.85731 13.0878L9.74989 17.2257C10.1448 17.6455 10.8118 17.6455 11.2066 17.2257L18.1427 9.85252C18.6244 9.34044 18.6244 8.54191 18.1427 8.02984C17.6175 7.47154 16.7303 7.47154 16.2051 8.02984L11.061 13.4982C10.7451 13.834 10.2115 13.834 9.89557 13.4982Z" fill="black"/>
                                </svg>
                              </span>
                            }
                          </div>
                        </Dropdown.Item>
                        <Dropdown.Item bsPrefix="menu-item px-2" onClick={() => executeChangeCardValue("expenses", "total")}>
                          <div className="menu-link fw-bold px-2 py-1 d-flex align-items-center justify-content-between">
                            {lang.labels.total}
                            { cardValue.expenses === "total" &&
                              <span className="svg-icon svg-icon-4 svg-icon-primary">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M9.89557 13.4982L7.79487 11.2651C7.26967 10.7068 6.38251 10.7068 5.85731 11.2651C5.37559 11.7772 5.37559 12.5757 5.85731 13.0878L9.74989 17.2257C10.1448 17.6455 10.8118 17.6455 11.2066 17.2257L18.1427 9.85252C18.6244 9.34044 18.6244 8.54191 18.1427 8.02984C17.6175 7.47154 16.7303 7.47154 16.2051 8.02984L11.061 13.4982C10.7451 13.834 10.2115 13.834 9.89557 13.4982Z" fill="black"/>
                                </svg>
                              </span>
                            }
                          </div>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                  <div className="d-flex flex-column my-3">
                    <span className="fw-semibold fs-3x text-gray-800 lh-1 ls-0">
                      {cardValue.expenses === "total" && dashboardInsight.general.expenses.total}
                      {cardValue.expenses === "abbreviation" && dashboardInsight.general.expenses.abbreviation}
                    </span>
                    <div className="m-0">
                      <span className="fw-semibold text-gray-600">{lang.labels.expenses}</span>
                    </div>
                  </div>
                  { dashboardInsight.general.expenses.trend === "0%"
                    ?
                    <span className="badge badge-light-warning fs-base d-flex align-items-end">
                      <span className="svg-icon svg-icon-5 svg-icon-warning ms-n1 me-1">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M7 6.59998V20C7 20.6 7.4 21 8 21C8.6 21 9 20.6 9 20V6.59998H7ZM15 17.4V4C15 3.4 15.4 3 16 3C16.6 3 17 3.4 17 4V17.4H15Z" fill="black"/>
                          <path opacity="0.3" d="M3 6.59999H13L8.7 2.3C8.3 1.9 7.7 1.9 7.3 2.3L3 6.59999ZM11 17.4H21L16.7 21.7C16.3 22.1 15.7 22.1 15.3 21.7L11 17.4Z" fill="black"/>
                        </svg>
                      </span>
                      {dashboardInsight.general.expenses.trend}
                    </span>
                    :
                    <>
                      { dashboardInsight.general.expenses.trend.charAt(0) === "-" &&
                        <span className="badge badge-light-danger fs-base d-flex align-items-end">
                          <span className="svg-icon svg-icon-5 svg-icon-danger ms-n1 me-1">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect opacity="0.5" x="11" y="18" width="13" height="2" rx="1" transform="rotate(-90 11 18)" fill="currentColor"></rect>
                              <path d="M11.4343 15.4343L7.25 11.25C6.83579 10.8358 6.16421 10.8358 5.75 11.25C5.33579 11.6642 5.33579 12.3358 5.75 12.75L11.2929 18.2929C11.6834 18.6834 12.3166 18.6834 12.7071 18.2929L18.25 12.75C18.6642 12.3358 18.6642 11.6642 18.25 11.25C17.8358 10.8358 17.1642 10.8358 16.75 11.25L12.5657 15.4343C12.2533 15.7467 11.7467 15.7467 11.4343 15.4343Z" fill="currentColor"></path>
                            </svg>
                          </span>
                          {dashboardInsight.general.expenses.trend.slice(1, dashboardInsight.general.expenses.trend.length)}
                        </span>
                      }
                      { dashboardInsight.general.expenses.trend.charAt(0) === "+" &&
                        <span className="badge badge-light-success fs-base d-flex align-items-end">
                          <span className="svg-icon svg-icon-5 svg-icon-success ms-n1 me-1">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect opacity="0.5" x="13" y="6" width="13" height="2" rx="1" transform="rotate(90 13 6)" fill="currentColor"></rect>
                              <path d="M12.5657 8.56569L16.75 12.75C17.1642 13.1642 17.8358 13.1642 18.25 12.75C18.6642 12.3358 18.6642 11.6642 18.25 11.25L12.7071 5.70711C12.3166 5.31658 11.6834 5.31658 11.2929 5.70711L5.75 11.25C5.33579 11.6642 5.33579 12.3358 5.75 12.75C6.16421 13.1642 6.83579 13.1642 7.25 12.75L11.4343 8.56569C11.7467 8.25327 12.2533 8.25327 12.5657 8.56569Z" fill="currentColor"></path>
                            </svg>
                          </span>
                          {dashboardInsight.general.expenses.trend.slice(1, dashboardInsight.general.expenses.trend.length)}
                        </span>
                      }
                    </>
                  }
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-xl-2">
              <div className="card h-lg-100">
                <div className="card-body d-flex justify-content-between align-items-start flex-column p-4">
                  <div className="d-flex justify-content-between align-items-start w-100">
                    <span className="svg-icon svg-icon-2hx svg-icon-gray-500">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path opacity="0.3" d="M21.25 18.525L13.05 21.825C12.35 22.125 11.65 22.125 10.95 21.825L2.75 18.525C1.75 18.125 1.75 16.725 2.75 16.325L4.04999 15.825L10.25 18.325C10.85 18.525 11.45 18.625 12.05 18.625C12.65 18.625 13.25 18.525 13.85 18.325L20.05 15.825L21.35 16.325C22.35 16.725 22.35 18.125 21.25 18.525ZM13.05 16.425L21.25 13.125C22.25 12.725 22.25 11.325 21.25 10.925L13.05 7.62502C12.35 7.32502 11.65 7.32502 10.95 7.62502L2.75 10.925C1.75 11.325 1.75 12.725 2.75 13.125L10.95 16.425C11.65 16.725 12.45 16.725 13.05 16.425Z" fill="currentColor"></path>
                        <path d="M11.05 11.025L2.84998 7.725C1.84998 7.325 1.84998 5.925 2.84998 5.525L11.05 2.225C11.75 1.925 12.45 1.925 13.15 2.225L21.35 5.525C22.35 5.925 22.35 7.325 21.35 7.725L13.05 11.025C12.45 11.325 11.65 11.325 11.05 11.025Z" fill="currentColor"></path>
                      </svg>
                    </span>
                    <Dropdown>
                      <Dropdown.Toggle variant="btn btn-icon btn-color-gray-500 btn-active-color-primary w-auto h-auto">
                        <span className="svg-icon svg-icon-1">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="4" fill="currentColor"></rect>
                            <rect x="11" y="11" width="2.6" height="2.6" rx="1.3" fill="currentColor"></rect>
                            <rect x="15" y="11" width="2.6" height="2.6" rx="1.3" fill="currentColor"></rect>
                            <rect x="7" y="11" width="2.6" height="2.6" rx="1.3" fill="currentColor"></rect>
                          </svg>
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-700 menu-state-bg-light-primary fw-bold fs-8 py-2" align="end">
                        <Dropdown.Item bsPrefix="menu-item px-2" onClick={() => executeChangeCardValue("turnout", "abbreviation")}>
                          <div className="menu-link fw-bold px-2 py-1 d-flex align-items-center justify-content-between">
                            {lang.labels.abbreviation}
                            { cardValue.turnout === "abbreviation" &&
                              <span className="svg-icon svg-icon-4 svg-icon-primary">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M9.89557 13.4982L7.79487 11.2651C7.26967 10.7068 6.38251 10.7068 5.85731 11.2651C5.37559 11.7772 5.37559 12.5757 5.85731 13.0878L9.74989 17.2257C10.1448 17.6455 10.8118 17.6455 11.2066 17.2257L18.1427 9.85252C18.6244 9.34044 18.6244 8.54191 18.1427 8.02984C17.6175 7.47154 16.7303 7.47154 16.2051 8.02984L11.061 13.4982C10.7451 13.834 10.2115 13.834 9.89557 13.4982Z" fill="black"/>
                                </svg>
                              </span>
                            }
                          </div>
                        </Dropdown.Item>
                        <Dropdown.Item bsPrefix="menu-item px-2" onClick={() => executeChangeCardValue("turnout", "total")}>
                          <div className="menu-link fw-bold px-2 py-1 d-flex align-items-center justify-content-between">
                            {lang.labels.total}
                            { cardValue.turnout === "total" &&
                              <span className="svg-icon svg-icon-4 svg-icon-primary">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M9.89557 13.4982L7.79487 11.2651C7.26967 10.7068 6.38251 10.7068 5.85731 11.2651C5.37559 11.7772 5.37559 12.5757 5.85731 13.0878L9.74989 17.2257C10.1448 17.6455 10.8118 17.6455 11.2066 17.2257L18.1427 9.85252C18.6244 9.34044 18.6244 8.54191 18.1427 8.02984C17.6175 7.47154 16.7303 7.47154 16.2051 8.02984L11.061 13.4982C10.7451 13.834 10.2115 13.834 9.89557 13.4982Z" fill="black"/>
                                </svg>
                              </span>
                            }
                          </div>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                  <div className="d-flex flex-column my-3">
                    <span className="fw-semibold fs-3x text-gray-800 lh-1 ls-0">
                      {cardValue.turnout ==='total' && dashboardInsight.general.turnout.total}
                      {cardValue.turnout ==='abbreviation' && dashboardInsight.general.turnout.abbreviation}
                    </span>
                    <div className="m-0">
                      <span className="fw-semibold text-gray-600">{lang.labels.turnout}</span>
                    </div>
                  </div>
                  { dashboardInsight.general.turnout.trend === "0%"
                    ?
                    <span className="badge badge-light-warning fs-base d-flex align-items-end">
                      <span className="svg-icon svg-icon-5 svg-icon-warning ms-n1 me-1">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M7 6.59998V20C7 20.6 7.4 21 8 21C8.6 21 9 20.6 9 20V6.59998H7ZM15 17.4V4C15 3.4 15.4 3 16 3C16.6 3 17 3.4 17 4V17.4H15Z" fill="black"/>
                          <path opacity="0.3" d="M3 6.59999H13L8.7 2.3C8.3 1.9 7.7 1.9 7.3 2.3L3 6.59999ZM11 17.4H21L16.7 21.7C16.3 22.1 15.7 22.1 15.3 21.7L11 17.4Z" fill="black"/>
                        </svg>
                      </span>
                      {dashboardInsight.general.turnout.trend}
                    </span>
                    :
                    <>
                      { dashboardInsight.general.turnout.trend.charAt(0) === "-" &&
                        <span className="badge badge-light-danger fs-base d-flex align-items-end">
                          <span className="svg-icon svg-icon-5 svg-icon-danger ms-n1 me-1">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect opacity="0.5" x="11" y="18" width="13" height="2" rx="1" transform="rotate(-90 11 18)" fill="currentColor"></rect>
                              <path d="M11.4343 15.4343L7.25 11.25C6.83579 10.8358 6.16421 10.8358 5.75 11.25C5.33579 11.6642 5.33579 12.3358 5.75 12.75L11.2929 18.2929C11.6834 18.6834 12.3166 18.6834 12.7071 18.2929L18.25 12.75C18.6642 12.3358 18.6642 11.6642 18.25 11.25C17.8358 10.8358 17.1642 10.8358 16.75 11.25L12.5657 15.4343C12.2533 15.7467 11.7467 15.7467 11.4343 15.4343Z" fill="currentColor"></path>
                            </svg>
                          </span>
                          {dashboardInsight.general.turnout.trend.slice(1, dashboardInsight.general.turnout.trend.length)}
                        </span>
                      }
                      { dashboardInsight.general.turnout.trend.charAt(0) === "+" &&
                        <span className="badge badge-light-success fs-base d-flex align-items-end">
                          <span className="svg-icon svg-icon-5 svg-icon-success ms-n1 me-1">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect opacity="0.5" x="13" y="6" width="13" height="2" rx="1" transform="rotate(90 13 6)" fill="currentColor"></rect>
                              <path d="M12.5657 8.56569L16.75 12.75C17.1642 13.1642 17.8358 13.1642 18.25 12.75C18.6642 12.3358 18.6642 11.6642 18.25 11.25L12.7071 5.70711C12.3166 5.31658 11.6834 5.31658 11.2929 5.70711L5.75 11.25C5.33579 11.6642 5.33579 12.3358 5.75 12.75C6.16421 13.1642 6.83579 13.1642 7.25 12.75L11.4343 8.56569C11.7467 8.25327 12.2533 8.25327 12.5657 8.56569Z" fill="currentColor"></path>
                            </svg>
                          </span>
                          {dashboardInsight.general.turnout.trend.slice(1, dashboardInsight.general.turnout.trend.length)}
                        </span>
                      }
                    </>
                  }
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-xl-2">
              <div className="card h-lg-100">
                <div className="card-body d-flex justify-content-between align-items-start flex-column p-4">
                  <div className="d-flex justify-content-between align-items-start w-100">
                    <span className="svg-icon svg-icon-2hx svg-icon-gray-500">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path opacity="0.3" d="M21.25 18.525L13.05 21.825C12.35 22.125 11.65 22.125 10.95 21.825L2.75 18.525C1.75 18.125 1.75 16.725 2.75 16.325L4.04999 15.825L10.25 18.325C10.85 18.525 11.45 18.625 12.05 18.625C12.65 18.625 13.25 18.525 13.85 18.325L20.05 15.825L21.35 16.325C22.35 16.725 22.35 18.125 21.25 18.525ZM13.05 16.425L21.25 13.125C22.25 12.725 22.25 11.325 21.25 10.925L13.05 7.62502C12.35 7.32502 11.65 7.32502 10.95 7.62502L2.75 10.925C1.75 11.325 1.75 12.725 2.75 13.125L10.95 16.425C11.65 16.725 12.45 16.725 13.05 16.425Z" fill="currentColor"></path>
                        <path d="M11.05 11.025L2.84998 7.725C1.84998 7.325 1.84998 5.925 2.84998 5.525L11.05 2.225C11.75 1.925 12.45 1.925 13.15 2.225L21.35 5.525C22.35 5.925 22.35 7.325 21.35 7.725L13.05 11.025C12.45 11.325 11.65 11.325 11.05 11.025Z" fill="currentColor"></path>
                      </svg>
                    </span>
                    <Dropdown>
                      <Dropdown.Toggle variant="btn btn-icon btn-color-gray-500 btn-active-color-primary w-auto h-auto">
                        <span className="svg-icon svg-icon-1">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="4" fill="currentColor"></rect>
                            <rect x="11" y="11" width="2.6" height="2.6" rx="1.3" fill="currentColor"></rect>
                            <rect x="15" y="11" width="2.6" height="2.6" rx="1.3" fill="currentColor"></rect>
                            <rect x="7" y="11" width="2.6" height="2.6" rx="1.3" fill="currentColor"></rect>
                          </svg>
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-700 menu-state-bg-light-primary fw-bold fs-8 py-2" align="end">
                        <Dropdown.Item bsPrefix="menu-item px-2" onClick={() => executeChangeCardValue("amount_orders_to_billed", "abbreviation")}>
                          <div className="menu-link fw-bold px-2 py-1 d-flex align-items-center justify-content-between">
                            {lang.labels.abbreviation}
                            { cardValue.amount_orders_to_billed === "abbreviation" &&
                              <span className="svg-icon svg-icon-4 svg-icon-primary">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M9.89557 13.4982L7.79487 11.2651C7.26967 10.7068 6.38251 10.7068 5.85731 11.2651C5.37559 11.7772 5.37559 12.5757 5.85731 13.0878L9.74989 17.2257C10.1448 17.6455 10.8118 17.6455 11.2066 17.2257L18.1427 9.85252C18.6244 9.34044 18.6244 8.54191 18.1427 8.02984C17.6175 7.47154 16.7303 7.47154 16.2051 8.02984L11.061 13.4982C10.7451 13.834 10.2115 13.834 9.89557 13.4982Z" fill="black"/>
                                </svg>
                              </span>
                            }
                          </div>
                        </Dropdown.Item>
                        <Dropdown.Item bsPrefix="menu-item px-2" onClick={() => executeChangeCardValue("amount_orders_to_billed", "total")}>
                          <div className="menu-link fw-bold px-2 py-1 d-flex align-items-center justify-content-between">
                            {lang.labels.total}
                            { cardValue.amount_orders_to_billed === "total" &&
                              <span className="svg-icon svg-icon-4 svg-icon-primary">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M9.89557 13.4982L7.79487 11.2651C7.26967 10.7068 6.38251 10.7068 5.85731 11.2651C5.37559 11.7772 5.37559 12.5757 5.85731 13.0878L9.74989 17.2257C10.1448 17.6455 10.8118 17.6455 11.2066 17.2257L18.1427 9.85252C18.6244 9.34044 18.6244 8.54191 18.1427 8.02984C17.6175 7.47154 16.7303 7.47154 16.2051 8.02984L11.061 13.4982C10.7451 13.834 10.2115 13.834 9.89557 13.4982Z" fill="black"/>
                                </svg>
                              </span>
                            }
                          </div>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                  <div className="d-flex flex-column my-3">
                    <span className="fw-semibold fs-3x text-gray-800 lh-1 ls-0">
                      {cardValue.amount_orders_to_billed ==='total' && dashboardInsight.general.amount_orders_to_billed.total}
                      {cardValue.amount_orders_to_billed ==='abbreviation' && dashboardInsight.general.amount_orders_to_billed.abbreviation}
                    </span>
                    <div className="m-0">
                      <span className="fw-semibold text-gray-600">{lang.labels.ordersToBilled}</span>
                    </div>
                  </div>
                  { dashboardInsight.general.amount_orders_to_billed.trend === "0%"
                    ?
                    <span className="badge badge-light-warning fs-base d-flex align-items-end">
                      <span className="svg-icon svg-icon-5 svg-icon-warning ms-n1 me-1">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M7 6.59998V20C7 20.6 7.4 21 8 21C8.6 21 9 20.6 9 20V6.59998H7ZM15 17.4V4C15 3.4 15.4 3 16 3C16.6 3 17 3.4 17 4V17.4H15Z" fill="black"/>
                          <path opacity="0.3" d="M3 6.59999H13L8.7 2.3C8.3 1.9 7.7 1.9 7.3 2.3L3 6.59999ZM11 17.4H21L16.7 21.7C16.3 22.1 15.7 22.1 15.3 21.7L11 17.4Z" fill="black"/>
                        </svg>
                      </span>
                      {dashboardInsight.general.amount_orders_to_billed.trend}
                    </span>
                    :
                    <>
                      { dashboardInsight.general.amount_orders_to_billed.trend.charAt(0) === "-" &&
                        <span className="badge badge-light-danger fs-base d-flex align-items-end">
                          <span className="svg-icon svg-icon-5 svg-icon-danger ms-n1 me-1">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect opacity="0.5" x="11" y="18" width="13" height="2" rx="1" transform="rotate(-90 11 18)" fill="currentColor"></rect>
                              <path d="M11.4343 15.4343L7.25 11.25C6.83579 10.8358 6.16421 10.8358 5.75 11.25C5.33579 11.6642 5.33579 12.3358 5.75 12.75L11.2929 18.2929C11.6834 18.6834 12.3166 18.6834 12.7071 18.2929L18.25 12.75C18.6642 12.3358 18.6642 11.6642 18.25 11.25C17.8358 10.8358 17.1642 10.8358 16.75 11.25L12.5657 15.4343C12.2533 15.7467 11.7467 15.7467 11.4343 15.4343Z" fill="currentColor"></path>
                            </svg>
                          </span>
                          {dashboardInsight.general.amount_orders_to_billed.trend.slice(1, dashboardInsight.general.amount_orders_to_billed.trend.length)}
                        </span>
                      }
                      { dashboardInsight.general.amount_orders_to_billed.trend.charAt(0) === "+" &&
                        <span className="badge badge-light-success fs-base d-flex align-items-end">
                          <span className="svg-icon svg-icon-5 svg-icon-success ms-n1 me-1">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect opacity="0.5" x="13" y="6" width="13" height="2" rx="1" transform="rotate(90 13 6)" fill="currentColor"></rect>
                              <path d="M12.5657 8.56569L16.75 12.75C17.1642 13.1642 17.8358 13.1642 18.25 12.75C18.6642 12.3358 18.6642 11.6642 18.25 11.25L12.7071 5.70711C12.3166 5.31658 11.6834 5.31658 11.2929 5.70711L5.75 11.25C5.33579 11.6642 5.33579 12.3358 5.75 12.75C6.16421 13.1642 6.83579 13.1642 7.25 12.75L11.4343 8.56569C11.7467 8.25327 12.2533 8.25327 12.5657 8.56569Z" fill="currentColor"></path>
                            </svg>
                          </span>
                          {dashboardInsight.general.amount_orders_to_billed.trend.slice(1, dashboardInsight.general.amount_orders_to_billed.trend.length)}
                        </span>
                      }
                    </>
                  }
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-xl-2">
              <div className="card h-lg-100">
                <div className="card-body d-flex justify-content-between align-items-start flex-column p-4">
                  <div className="d-flex justify-content-between align-items-start w-100">
                    <span className="svg-icon svg-icon-2hx svg-icon-gray-500">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path opacity="0.3" d="M21.25 18.525L13.05 21.825C12.35 22.125 11.65 22.125 10.95 21.825L2.75 18.525C1.75 18.125 1.75 16.725 2.75 16.325L4.04999 15.825L10.25 18.325C10.85 18.525 11.45 18.625 12.05 18.625C12.65 18.625 13.25 18.525 13.85 18.325L20.05 15.825L21.35 16.325C22.35 16.725 22.35 18.125 21.25 18.525ZM13.05 16.425L21.25 13.125C22.25 12.725 22.25 11.325 21.25 10.925L13.05 7.62502C12.35 7.32502 11.65 7.32502 10.95 7.62502L2.75 10.925C1.75 11.325 1.75 12.725 2.75 13.125L10.95 16.425C11.65 16.725 12.45 16.725 13.05 16.425Z" fill="currentColor"></path>
                        <path d="M11.05 11.025L2.84998 7.725C1.84998 7.325 1.84998 5.925 2.84998 5.525L11.05 2.225C11.75 1.925 12.45 1.925 13.15 2.225L21.35 5.525C22.35 5.925 22.35 7.325 21.35 7.725L13.05 11.025C12.45 11.325 11.65 11.325 11.05 11.025Z" fill="currentColor"></path>
                      </svg>
                    </span>
                    <Dropdown>
                      <Dropdown.Toggle variant="btn btn-icon btn-color-gray-500 btn-active-color-primary w-auto h-auto">
                        <span className="svg-icon svg-icon-1">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="4" fill="currentColor"></rect>
                            <rect x="11" y="11" width="2.6" height="2.6" rx="1.3" fill="currentColor"></rect>
                            <rect x="15" y="11" width="2.6" height="2.6" rx="1.3" fill="currentColor"></rect>
                            <rect x="7" y="11" width="2.6" height="2.6" rx="1.3" fill="currentColor"></rect>
                          </svg>
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-700 menu-state-bg-light-primary fw-bold fs-8 py-2" align="end">
                        <Dropdown.Item bsPrefix="menu-item px-2" onClick={() => executeChangeCardValue("amount_debt", "abbreviation")}>
                          <div className="menu-link fw-bold px-2 py-1 d-flex align-items-center justify-content-between">
                            {lang.labels.abbreviation}
                            { cardValue.amount_debt === "abbreviation" &&
                              <span className="svg-icon svg-icon-4 svg-icon-primary">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M9.89557 13.4982L7.79487 11.2651C7.26967 10.7068 6.38251 10.7068 5.85731 11.2651C5.37559 11.7772 5.37559 12.5757 5.85731 13.0878L9.74989 17.2257C10.1448 17.6455 10.8118 17.6455 11.2066 17.2257L18.1427 9.85252C18.6244 9.34044 18.6244 8.54191 18.1427 8.02984C17.6175 7.47154 16.7303 7.47154 16.2051 8.02984L11.061 13.4982C10.7451 13.834 10.2115 13.834 9.89557 13.4982Z" fill="black"/>
                                </svg>
                              </span>
                            }
                          </div>
                        </Dropdown.Item>
                        <Dropdown.Item bsPrefix="menu-item px-2" onClick={() => executeChangeCardValue("amount_debt", "percentage")}>
                          <div className="menu-link fw-bold px-2 py-1 d-flex align-items-center justify-content-between">
                          {lang.labels.percentage}
                            { cardValue.amount_debt === "percentage" &&
                              <span className="svg-icon svg-icon-4 svg-icon-primary">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M9.89557 13.4982L7.79487 11.2651C7.26967 10.7068 6.38251 10.7068 5.85731 11.2651C5.37559 11.7772 5.37559 12.5757 5.85731 13.0878L9.74989 17.2257C10.1448 17.6455 10.8118 17.6455 11.2066 17.2257L18.1427 9.85252C18.6244 9.34044 18.6244 8.54191 18.1427 8.02984C17.6175 7.47154 16.7303 7.47154 16.2051 8.02984L11.061 13.4982C10.7451 13.834 10.2115 13.834 9.89557 13.4982Z" fill="black"/>
                                </svg>
                              </span>
                            }
                          </div>
                        </Dropdown.Item>
                        <Dropdown.Item bsPrefix="menu-item px-2" onClick={() => executeChangeCardValue("amount_debt", "total")}>
                          <div className="menu-link fw-bold px-2 py-1 d-flex align-items-center justify-content-between">
                            {lang.labels.total}
                            { cardValue.amount_debt === "total" &&
                              <span className="svg-icon svg-icon-4 svg-icon-primary">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M9.89557 13.4982L7.79487 11.2651C7.26967 10.7068 6.38251 10.7068 5.85731 11.2651C5.37559 11.7772 5.37559 12.5757 5.85731 13.0878L9.74989 17.2257C10.1448 17.6455 10.8118 17.6455 11.2066 17.2257L18.1427 9.85252C18.6244 9.34044 18.6244 8.54191 18.1427 8.02984C17.6175 7.47154 16.7303 7.47154 16.2051 8.02984L11.061 13.4982C10.7451 13.834 10.2115 13.834 9.89557 13.4982Z" fill="black"/>
                                </svg>
                              </span>
                            }
                          </div>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                  <div className="d-flex flex-column my-3">
                    <span className="fw-semibold fs-3x text-gray-800 lh-1 ls-0">
                      {cardValue.amount_debt === "total" && dashboardInsight.general.amount_debt.total}
                      {cardValue.amount_debt === "percentage" && dashboardInsight.general.amount_debt.percentage}
                      {cardValue.amount_debt === "abbreviation" && dashboardInsight.general.amount_debt.abbreviation}
                    </span>
                    <div className="m-0">
                      <span className="fw-semibold text-gray-600">{lang.labels.amountDebt}</span>
                    </div>
                  </div>
                  { dashboardInsight.general.amount_debt.trend === "0%"
                    ?
                    <span className="badge badge-light-warning fs-base d-flex align-items-end">
                      <span className="svg-icon svg-icon-5 svg-icon-warning ms-n1 me-1">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M7 6.59998V20C7 20.6 7.4 21 8 21C8.6 21 9 20.6 9 20V6.59998H7ZM15 17.4V4C15 3.4 15.4 3 16 3C16.6 3 17 3.4 17 4V17.4H15Z" fill="black"/>
                          <path opacity="0.3" d="M3 6.59999H13L8.7 2.3C8.3 1.9 7.7 1.9 7.3 2.3L3 6.59999ZM11 17.4H21L16.7 21.7C16.3 22.1 15.7 22.1 15.3 21.7L11 17.4Z" fill="black"/>
                        </svg>
                      </span>
                      {dashboardInsight.general.amount_debt.trend}
                    </span>
                    :
                    <>
                      { dashboardInsight.general.amount_debt.trend.charAt(0) === "-" &&
                        <span className="badge badge-light-danger fs-base d-flex align-items-end">
                          <span className="svg-icon svg-icon-5 svg-icon-danger ms-n1 me-1">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect opacity="0.5" x="11" y="18" width="13" height="2" rx="1" transform="rotate(-90 11 18)" fill="currentColor"></rect>
                              <path d="M11.4343 15.4343L7.25 11.25C6.83579 10.8358 6.16421 10.8358 5.75 11.25C5.33579 11.6642 5.33579 12.3358 5.75 12.75L11.2929 18.2929C11.6834 18.6834 12.3166 18.6834 12.7071 18.2929L18.25 12.75C18.6642 12.3358 18.6642 11.6642 18.25 11.25C17.8358 10.8358 17.1642 10.8358 16.75 11.25L12.5657 15.4343C12.2533 15.7467 11.7467 15.7467 11.4343 15.4343Z" fill="currentColor"></path>
                            </svg>
                          </span>
                          {dashboardInsight.general.amount_debt.trend.slice(1, dashboardInsight.general.amount_debt.trend.length)}
                        </span>
                      }
                      { dashboardInsight.general.amount_debt.trend.charAt(0) === "+" &&
                        <span className="badge badge-light-success fs-base d-flex align-items-end">
                          <span className="svg-icon svg-icon-5 svg-icon-success ms-n1 me-1">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect opacity="0.5" x="13" y="6" width="13" height="2" rx="1" transform="rotate(90 13 6)" fill="currentColor"></rect>
                              <path d="M12.5657 8.56569L16.75 12.75C17.1642 13.1642 17.8358 13.1642 18.25 12.75C18.6642 12.3358 18.6642 11.6642 18.25 11.25L12.7071 5.70711C12.3166 5.31658 11.6834 5.31658 11.2929 5.70711L5.75 11.25C5.33579 11.6642 5.33579 12.3358 5.75 12.75C6.16421 13.1642 6.83579 13.1642 7.25 12.75L11.4343 8.56569C11.7467 8.25327 12.2533 8.25327 12.5657 8.56569Z" fill="currentColor"></path>
                            </svg>
                          </span>
                          {dashboardInsight.general.amount_debt.trend.slice(1, dashboardInsight.general.amount_debt.trend.length)}
                        </span>
                      }
                    </>
                  }
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-xl-2">
              <div className="card h-lg-100">
                <div className="card-body d-flex justify-content-between align-items-start flex-column p-4">
                  <div className="d-flex justify-content-between align-items-start w-100">
                    <span className="svg-icon svg-icon-2hx svg-icon-gray-500">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path opacity="0.3" d="M21.25 18.525L13.05 21.825C12.35 22.125 11.65 22.125 10.95 21.825L2.75 18.525C1.75 18.125 1.75 16.725 2.75 16.325L4.04999 15.825L10.25 18.325C10.85 18.525 11.45 18.625 12.05 18.625C12.65 18.625 13.25 18.525 13.85 18.325L20.05 15.825L21.35 16.325C22.35 16.725 22.35 18.125 21.25 18.525ZM13.05 16.425L21.25 13.125C22.25 12.725 22.25 11.325 21.25 10.925L13.05 7.62502C12.35 7.32502 11.65 7.32502 10.95 7.62502L2.75 10.925C1.75 11.325 1.75 12.725 2.75 13.125L10.95 16.425C11.65 16.725 12.45 16.725 13.05 16.425Z" fill="currentColor"></path>
                        <path d="M11.05 11.025L2.84998 7.725C1.84998 7.325 1.84998 5.925 2.84998 5.525L11.05 2.225C11.75 1.925 12.45 1.925 13.15 2.225L21.35 5.525C22.35 5.925 22.35 7.325 21.35 7.725L13.05 11.025C12.45 11.325 11.65 11.325 11.05 11.025Z" fill="currentColor"></path>
                      </svg>
                    </span>
                    <Dropdown>
                      <Dropdown.Toggle variant="btn btn-icon btn-color-gray-500 btn-active-color-primary w-auto h-auto">
                        <span className="svg-icon svg-icon-1">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="4" fill="currentColor"></rect>
                            <rect x="11" y="11" width="2.6" height="2.6" rx="1.3" fill="currentColor"></rect>
                            <rect x="15" y="11" width="2.6" height="2.6" rx="1.3" fill="currentColor"></rect>
                            <rect x="7" y="11" width="2.6" height="2.6" rx="1.3" fill="currentColor"></rect>
                          </svg>
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-700 menu-state-bg-light-primary fw-bold fs-8 py-2" align="end">
                        <Dropdown.Item bsPrefix="menu-item px-2" onClick={() => executeChangeCardValue("amount_paid", "abbreviation")}>
                          <div className="menu-link fw-bold px-2 py-1 d-flex align-items-center justify-content-between">
                            {lang.labels.abbreviation}
                            { cardValue.amount_paid === "abbreviation" &&
                              <span className="svg-icon svg-icon-4 svg-icon-primary">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M9.89557 13.4982L7.79487 11.2651C7.26967 10.7068 6.38251 10.7068 5.85731 11.2651C5.37559 11.7772 5.37559 12.5757 5.85731 13.0878L9.74989 17.2257C10.1448 17.6455 10.8118 17.6455 11.2066 17.2257L18.1427 9.85252C18.6244 9.34044 18.6244 8.54191 18.1427 8.02984C17.6175 7.47154 16.7303 7.47154 16.2051 8.02984L11.061 13.4982C10.7451 13.834 10.2115 13.834 9.89557 13.4982Z" fill="black"/>
                                </svg>
                              </span>
                            }
                          </div>
                        </Dropdown.Item>
                        <Dropdown.Item bsPrefix="menu-item px-2" onClick={() => executeChangeCardValue("amount_paid", "percentage")}>
                          <div className="menu-link fw-bold px-2 py-1 d-flex align-items-center justify-content-between">
                            {lang.labels.percentage}
                            { cardValue.amount_paid === "percentage" &&
                              <span className="svg-icon svg-icon-4 svg-icon-primary">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M9.89557 13.4982L7.79487 11.2651C7.26967 10.7068 6.38251 10.7068 5.85731 11.2651C5.37559 11.7772 5.37559 12.5757 5.85731 13.0878L9.74989 17.2257C10.1448 17.6455 10.8118 17.6455 11.2066 17.2257L18.1427 9.85252C18.6244 9.34044 18.6244 8.54191 18.1427 8.02984C17.6175 7.47154 16.7303 7.47154 16.2051 8.02984L11.061 13.4982C10.7451 13.834 10.2115 13.834 9.89557 13.4982Z" fill="black"/>
                                </svg>
                              </span>
                            }
                          </div>
                        </Dropdown.Item>
                        <Dropdown.Item bsPrefix="menu-item px-2" onClick={() => executeChangeCardValue("amount_paid", "total")}>
                          <div className="menu-link fw-bold px-2 py-1 d-flex align-items-center justify-content-between">
                            {lang.labels.total}
                            { cardValue.amount_paid === "total" &&
                              <span className="svg-icon svg-icon-4 svg-icon-primary">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M9.89557 13.4982L7.79487 11.2651C7.26967 10.7068 6.38251 10.7068 5.85731 11.2651C5.37559 11.7772 5.37559 12.5757 5.85731 13.0878L9.74989 17.2257C10.1448 17.6455 10.8118 17.6455 11.2066 17.2257L18.1427 9.85252C18.6244 9.34044 18.6244 8.54191 18.1427 8.02984C17.6175 7.47154 16.7303 7.47154 16.2051 8.02984L11.061 13.4982C10.7451 13.834 10.2115 13.834 9.89557 13.4982Z" fill="black"/>
                                </svg>
                              </span>
                            }
                          </div>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                  <div className="d-flex flex-column my-3">
                    <span className="fw-semibold fs-3x text-gray-800 lh-1 ls-0">
                      {cardValue.amount_paid === "total" &&  dashboardInsight.general.amount_paid.total}
                      {cardValue.amount_paid === "percentage" && dashboardInsight.general.amount_paid.percentage}
                      {cardValue.amount_paid === "abbreviation" && dashboardInsight.general.amount_paid.abbreviation}
                    </span>
                    <div className="m-0">
                      <span className="fw-semibold text-gray-600">{lang.labels.amountPaid}</span>
                    </div>
                  </div>
                  { dashboardInsight.general.amount_paid.trend === "0%"
                    ?
                    <span className="badge badge-light-warning fs-base d-flex align-items-end">
                      <span className="svg-icon svg-icon-5 svg-icon-warning ms-n1 me-1">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M7 6.59998V20C7 20.6 7.4 21 8 21C8.6 21 9 20.6 9 20V6.59998H7ZM15 17.4V4C15 3.4 15.4 3 16 3C16.6 3 17 3.4 17 4V17.4H15Z" fill="black"/>
                          <path opacity="0.3" d="M3 6.59999H13L8.7 2.3C8.3 1.9 7.7 1.9 7.3 2.3L3 6.59999ZM11 17.4H21L16.7 21.7C16.3 22.1 15.7 22.1 15.3 21.7L11 17.4Z" fill="black"/>
                        </svg>
                      </span>
                      {dashboardInsight.general.amount_paid.trend}
                    </span>
                    :
                    <>
                      { dashboardInsight.general.amount_paid.trend.charAt(0) === "-" &&
                        <span className="badge badge-light-danger fs-base d-flex align-items-end">
                          <span className="svg-icon svg-icon-5 svg-icon-danger ms-n1 me-1">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect opacity="0.5" x="11" y="18" width="13" height="2" rx="1" transform="rotate(-90 11 18)" fill="currentColor"></rect>
                              <path d="M11.4343 15.4343L7.25 11.25C6.83579 10.8358 6.16421 10.8358 5.75 11.25C5.33579 11.6642 5.33579 12.3358 5.75 12.75L11.2929 18.2929C11.6834 18.6834 12.3166 18.6834 12.7071 18.2929L18.25 12.75C18.6642 12.3358 18.6642 11.6642 18.25 11.25C17.8358 10.8358 17.1642 10.8358 16.75 11.25L12.5657 15.4343C12.2533 15.7467 11.7467 15.7467 11.4343 15.4343Z" fill="currentColor"></path>
                            </svg>
                          </span>
                          {dashboardInsight.general.amount_paid.trend.slice(1, dashboardInsight.general.amount_paid.trend.length)}
                        </span>
                      }
                      { dashboardInsight.general.amount_paid.trend.charAt(0) === "+" &&
                        <span className="badge badge-light-success fs-base d-flex align-items-end">
                          <span className="svg-icon svg-icon-5 svg-icon-success ms-n1 me-1">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect opacity="0.5" x="13" y="6" width="13" height="2" rx="1" transform="rotate(90 13 6)" fill="currentColor"></rect>
                              <path d="M12.5657 8.56569L16.75 12.75C17.1642 13.1642 17.8358 13.1642 18.25 12.75C18.6642 12.3358 18.6642 11.6642 18.25 11.25L12.7071 5.70711C12.3166 5.31658 11.6834 5.31658 11.2929 5.70711L5.75 11.25C5.33579 11.6642 5.33579 12.3358 5.75 12.75C6.16421 13.1642 6.83579 13.1642 7.25 12.75L11.4343 8.56569C11.7467 8.25327 12.2533 8.25327 12.5657 8.56569Z" fill="currentColor"></path>
                            </svg>
                          </span>
                          {dashboardInsight.general.amount_paid.trend.slice(1, dashboardInsight.general.amount_paid.trend.length)}
                        </span>
                      }
                    </>
                  }
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-xl-2">
              <div className="card h-lg-100">
                <div className="card-body d-flex justify-content-between align-items-start flex-column p-4">
                  <div className="d-flex justify-content-between align-items-start w-100">
                    <span className="svg-icon svg-icon-2hx svg-icon-gray-500">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path opacity="0.3" d="M21.25 18.525L13.05 21.825C12.35 22.125 11.65 22.125 10.95 21.825L2.75 18.525C1.75 18.125 1.75 16.725 2.75 16.325L4.04999 15.825L10.25 18.325C10.85 18.525 11.45 18.625 12.05 18.625C12.65 18.625 13.25 18.525 13.85 18.325L20.05 15.825L21.35 16.325C22.35 16.725 22.35 18.125 21.25 18.525ZM13.05 16.425L21.25 13.125C22.25 12.725 22.25 11.325 21.25 10.925L13.05 7.62502C12.35 7.32502 11.65 7.32502 10.95 7.62502L2.75 10.925C1.75 11.325 1.75 12.725 2.75 13.125L10.95 16.425C11.65 16.725 12.45 16.725 13.05 16.425Z" fill="currentColor"></path>
                        <path d="M11.05 11.025L2.84998 7.725C1.84998 7.325 1.84998 5.925 2.84998 5.525L11.05 2.225C11.75 1.925 12.45 1.925 13.15 2.225L21.35 5.525C22.35 5.925 22.35 7.325 21.35 7.725L13.05 11.025C12.45 11.325 11.65 11.325 11.05 11.025Z" fill="currentColor"></path>
                      </svg>
                    </span>
                    <Dropdown>
                      <Dropdown.Toggle variant="btn btn-icon btn-color-gray-500 btn-active-color-primary w-auto h-auto">
                        <span className="svg-icon svg-icon-1">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="4" fill="currentColor"></rect>
                            <rect x="11" y="11" width="2.6" height="2.6" rx="1.3" fill="currentColor"></rect>
                            <rect x="15" y="11" width="2.6" height="2.6" rx="1.3" fill="currentColor"></rect>
                            <rect x="7" y="11" width="2.6" height="2.6" rx="1.3" fill="currentColor"></rect>
                          </svg>
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-700 menu-state-bg-light-primary fw-bold fs-8 py-2" align="end">
                        <Dropdown.Item bsPrefix="menu-item px-2" onClick={() => executeChangeCardValue("quotes_approved", "abbreviation")}>
                          <div className="menu-link fw-bold px-2 py-1 d-flex align-items-center justify-content-between">
                            {lang.labels.abbreviation}
                            { cardValue.quotes_approved === "abbreviation" &&
                              <span className="svg-icon svg-icon-4 svg-icon-primary">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M9.89557 13.4982L7.79487 11.2651C7.26967 10.7068 6.38251 10.7068 5.85731 11.2651C5.37559 11.7772 5.37559 12.5757 5.85731 13.0878L9.74989 17.2257C10.1448 17.6455 10.8118 17.6455 11.2066 17.2257L18.1427 9.85252C18.6244 9.34044 18.6244 8.54191 18.1427 8.02984C17.6175 7.47154 16.7303 7.47154 16.2051 8.02984L11.061 13.4982C10.7451 13.834 10.2115 13.834 9.89557 13.4982Z" fill="black"/>
                                </svg>
                              </span>
                            }
                          </div>
                        </Dropdown.Item>
                        <Dropdown.Item bsPrefix="menu-item px-2" onClick={() => executeChangeCardValue("quotes_approved", "percentage")}>
                          <div className="menu-link fw-bold px-2 py-1 d-flex align-items-center justify-content-between">
                            {lang.labels.percentage}
                            { cardValue.quotes_approved === "percentage" &&
                              <span className="svg-icon svg-icon-4 svg-icon-primary">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M9.89557 13.4982L7.79487 11.2651C7.26967 10.7068 6.38251 10.7068 5.85731 11.2651C5.37559 11.7772 5.37559 12.5757 5.85731 13.0878L9.74989 17.2257C10.1448 17.6455 10.8118 17.6455 11.2066 17.2257L18.1427 9.85252C18.6244 9.34044 18.6244 8.54191 18.1427 8.02984C17.6175 7.47154 16.7303 7.47154 16.2051 8.02984L11.061 13.4982C10.7451 13.834 10.2115 13.834 9.89557 13.4982Z" fill="black"/>
                                </svg>
                              </span>
                            }
                          </div>
                        </Dropdown.Item>
                        <Dropdown.Item bsPrefix="menu-item px-2" onClick={() => executeChangeCardValue("quotes_approved", "total")}>
                          <div className="menu-link fw-bold px-2 py-1 d-flex align-items-center justify-content-between">
                            {lang.labels.total}
                            { cardValue.quotes_approved === "total" &&
                              <span className="svg-icon svg-icon-4 svg-icon-primary">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M9.89557 13.4982L7.79487 11.2651C7.26967 10.7068 6.38251 10.7068 5.85731 11.2651C5.37559 11.7772 5.37559 12.5757 5.85731 13.0878L9.74989 17.2257C10.1448 17.6455 10.8118 17.6455 11.2066 17.2257L18.1427 9.85252C18.6244 9.34044 18.6244 8.54191 18.1427 8.02984C17.6175 7.47154 16.7303 7.47154 16.2051 8.02984L11.061 13.4982C10.7451 13.834 10.2115 13.834 9.89557 13.4982Z" fill="black"/>
                                </svg>
                              </span>
                            }
                          </div>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                  <div className="d-flex flex-column my-3">
                    <span className="fw-semibold fs-3x text-gray-800 lh-1 ls-0">
                      {cardValue.quotes_approved === "total" && dashboardInsight.general.quotes_approved.total}
                      {cardValue.quotes_approved === "percentage" && dashboardInsight.general.quotes_approved.percentage}
                      {cardValue.quotes_approved === "abbreviation" && dashboardInsight.general.quotes_approved.abbreviation}
                    </span>
                    <div className="m-0">
                      <span className="fw-semibold text-gray-600">{lang.labels.quotesApproved}</span>
                    </div>
                  </div>
                  { dashboardInsight.general.quotes_approved.trend === "0%"
                    ?
                    <span className="badge badge-light-warning fs-base d-flex align-items-end">
                      <span className="svg-icon svg-icon-5 svg-icon-warning ms-n1 me-1">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M7 6.59998V20C7 20.6 7.4 21 8 21C8.6 21 9 20.6 9 20V6.59998H7ZM15 17.4V4C15 3.4 15.4 3 16 3C16.6 3 17 3.4 17 4V17.4H15Z" fill="black"/>
                          <path opacity="0.3" d="M3 6.59999H13L8.7 2.3C8.3 1.9 7.7 1.9 7.3 2.3L3 6.59999ZM11 17.4H21L16.7 21.7C16.3 22.1 15.7 22.1 15.3 21.7L11 17.4Z" fill="black"/>
                        </svg>
                      </span>
                      {dashboardInsight.general.quotes_approved.trend}
                    </span>
                    :
                    <>
                      { dashboardInsight.general.quotes_approved.trend.charAt(0) === "-" &&
                        <span className="badge badge-light-danger fs-base d-flex align-items-end">
                          <span className="svg-icon svg-icon-5 svg-icon-danger ms-n1 me-1">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect opacity="0.5" x="11" y="18" width="13" height="2" rx="1" transform="rotate(-90 11 18)" fill="currentColor"></rect>
                              <path d="M11.4343 15.4343L7.25 11.25C6.83579 10.8358 6.16421 10.8358 5.75 11.25C5.33579 11.6642 5.33579 12.3358 5.75 12.75L11.2929 18.2929C11.6834 18.6834 12.3166 18.6834 12.7071 18.2929L18.25 12.75C18.6642 12.3358 18.6642 11.6642 18.25 11.25C17.8358 10.8358 17.1642 10.8358 16.75 11.25L12.5657 15.4343C12.2533 15.7467 11.7467 15.7467 11.4343 15.4343Z" fill="currentColor"></path>
                            </svg>
                          </span>
                          {dashboardInsight.general.quotes_approved.trend.slice(1, dashboardInsight.general.quotes_approved.trend.length)}
                        </span>
                      }
                      { dashboardInsight.general.quotes_approved.trend.charAt(0) === "+" &&
                        <span className="badge badge-light-success fs-base d-flex align-items-end">
                          <span className="svg-icon svg-icon-5 svg-icon-success ms-n1 me-1">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect opacity="0.5" x="13" y="6" width="13" height="2" rx="1" transform="rotate(90 13 6)" fill="currentColor"></rect>
                              <path d="M12.5657 8.56569L16.75 12.75C17.1642 13.1642 17.8358 13.1642 18.25 12.75C18.6642 12.3358 18.6642 11.6642 18.25 11.25L12.7071 5.70711C12.3166 5.31658 11.6834 5.31658 11.2929 5.70711L5.75 11.25C5.33579 11.6642 5.33579 12.3358 5.75 12.75C6.16421 13.1642 6.83579 13.1642 7.25 12.75L11.4343 8.56569C11.7467 8.25327 12.2533 8.25327 12.5657 8.56569Z" fill="currentColor"></path>
                            </svg>
                          </span>
                          {dashboardInsight.general.quotes_approved.trend.slice(1, dashboardInsight.general.quotes_approved.trend.length)}
                        </span>
                      }
                    </>
                  }
                </div>
              </div>
            </div>
          </div>
          <div className="row g-2 g-xl-3 mb-10">
            <div className="col-xl-4 mb-3">
              <div className="card card-flush h-xl-50 mb-3">
                <div className="card-header pt-2">
                  <h3 className="card-title align-items-start flex-column">
                    <span className="card-label fw-bold text-dark">{lang.labels.quotesByState}</span>
                    <span className="text-gray-400 pt-1 fw-semibold fs-7">{lang.labels.distributionOfQuotes}</span>
                  </h3>
                </div>
                <div className="card-body py-0 px-5">
                  <AppChartGaugeQuote id={"chart-gauge-quotes"} info={dashboardInsight.quotes}></AppChartGaugeQuote>
                </div>
              </div>
              <div className="card card-flush h-xl-50">
                <div className="card-header pt-2">
                  <h3 className="card-title align-items-start flex-column">
                    <span className="card-label fw-bold text-dark">{lang.labels.ordersByState}</span>
                    <span className="text-gray-400 pt-1 fw-semibold fs-7">{lang.labels.distributionOfOrders}</span>
                  </h3>
                </div>
                <div className="card-body py-0 px-5">
                  <AppChartGaugeOrder id={"chart-gauge-orders"} info={dashboardInsight.orders}></AppChartGaugeOrder>
                </div>
              </div>
            </div>
            <div className="col-xl-8">
              <div className="card card-flush h-xl-100">
                <div className="card-header pt-2">
                  <h3 className="card-title align-items-start flex-column">
                    <span className="card-label fw-bold text-dark">{lang.labels.invoicesStillToBePaid}</span>
                    <span className="text-gray-400 pt-1 fw-semibold fs-7">{lang.labels.invoicesStillToBePaidByTheCustomer}</span>
                  </h3>
                </div>
                <div className="card-body py-0 px-5">
                  <AppChartBarInvoices id={"chart-bar-invoices"} companyForUser={companyForUser} info={dashboardInsight.invoices}></AppChartBarInvoices>
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex flex-wrap flex-stack mb-5 px-2">
            <h3 className="fw-bold">
              {lang.labels.comparisonIncomeAndExpenses}
              <span className="fs-6 text-gray-400 fw-semibold ms-1">{lang.labels.byYear}</span>
            </h3>
            <div className="d-flex flex-wrap align-items-center">
              <div className="ranges me-5">
                <Dropdown>
                  <Dropdown.Toggle variant="btn btn-sm btn-light bg-body border border-dark d-flex align-items-center justify-content-between py-1 ps-3 pe-1 w-100px">
                    <div className="text-gray-900 fw-bold">{range.current}</div>
                    <span className="svg-icon svg-icon-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M11.4343 12.7344L7.25 8.55005C6.83579 8.13583 6.16421 8.13584 5.75 8.55005C5.33579 8.96426 5.33579 9.63583 5.75 10.05L11.2929 15.5929C11.6834 15.9835 12.3166 15.9835 12.7071 15.5929L18.25 10.05C18.6642 9.63584 18.6642 8.96426 18.25 8.55005C17.8358 8.13584 17.1642 8.13584 16.75 8.55005L12.5657 12.7344C12.2533 13.0468 11.7467 13.0468 11.4343 12.7344Z" fill="black"></path>
                      </svg>
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu variant="daterangepicker ltr show-ranges opensleft" align="end">
                    <div className="ranges w-150px">
                      { range.list.map (( (item, index) => { return (
                        <Dropdown.Item key={index} active={item === range.current} onClick={() => executeSelectYearIntervalRange(item)}>
                          <p className="px-3 py-1 fw-bold m-0">{item}</p>
                        </Dropdown.Item>
                      )}))}
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
              <div className="btn btn-sm btn-icon btn-light btn-color-muted btn-active-primary active">
                <span className="svg-icon svg-icon-1">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path opacity="0.3" d="M21 22H3C2.4 22 2 21.6 2 21V5C2 4.4 2.4 4 3 4H21C21.6 4 22 4.4 22 5V21C22 21.6 21.6 22 21 22Z" fill="currentColor"></path>
                    <path d="M6 6C5.4 6 5 5.6 5 5V3C5 2.4 5.4 2 6 2C6.6 2 7 2.4 7 3V5C7 5.6 6.6 6 6 6ZM11 5V3C11 2.4 10.6 2 10 2C9.4 2 9 2.4 9 3V5C9 5.6 9.4 6 10 6C10.6 6 11 5.6 11 5ZM15 5V3C15 2.4 14.6 2 14 2C13.4 2 13 2.4 13 3V5C13 5.6 13.4 6 14 6C14.6 6 15 5.6 15 5ZM19 5V3C19 2.4 18.6 2 18 2C17.4 2 17 2.4 17 3V5C17 5.6 17.4 6 18 6C18.6 6 19 5.6 19 5Z" fill="currentColor"></path>
                    <path d="M8.8 13.1C9.2 13.1 9.5 13 9.7 12.8C9.9 12.6 10.1 12.3 10.1 11.9C10.1 11.6 10 11.3 9.8 11.1C9.6 10.9 9.3 10.8 9 10.8C8.8 10.8 8.59999 10.8 8.39999 10.9C8.19999 11 8.1 11.1 8 11.2C7.9 11.3 7.8 11.4 7.7 11.6C7.6 11.8 7.5 11.9 7.5 12.1C7.5 12.2 7.4 12.2 7.3 12.3C7.2 12.4 7.09999 12.4 6.89999 12.4C6.69999 12.4 6.6 12.3 6.5 12.2C6.4 12.1 6.3 11.9 6.3 11.7C6.3 11.5 6.4 11.3 6.5 11.1C6.6 10.9 6.8 10.7 7 10.5C7.2 10.3 7.49999 10.1 7.89999 10C8.29999 9.90003 8.60001 9.80003 9.10001 9.80003C9.50001 9.80003 9.80001 9.90003 10.1 10C10.4 10.1 10.7 10.3 10.9 10.4C11.1 10.5 11.3 10.8 11.4 11.1C11.5 11.4 11.6 11.6 11.6 11.9C11.6 12.3 11.5 12.6 11.3 12.9C11.1 13.2 10.9 13.5 10.6 13.7C10.9 13.9 11.2 14.1 11.4 14.3C11.6 14.5 11.8 14.7 11.9 15C12 15.3 12.1 15.5 12.1 15.8C12.1 16.2 12 16.5 11.9 16.8C11.8 17.1 11.5 17.4 11.3 17.7C11.1 18 10.7 18.2 10.3 18.3C9.9 18.4 9.5 18.5 9 18.5C8.5 18.5 8.1 18.4 7.7 18.2C7.3 18 7 17.8 6.8 17.6C6.6 17.4 6.4 17.1 6.3 16.8C6.2 16.5 6.10001 16.3 6.10001 16.1C6.10001 15.9 6.2 15.7 6.3 15.6C6.4 15.5 6.6 15.4 6.8 15.4C6.9 15.4 7.00001 15.4 7.10001 15.5C7.20001 15.6 7.3 15.6 7.3 15.7C7.5 16.2 7.7 16.6 8 16.9C8.3 17.2 8.6 17.3 9 17.3C9.2 17.3 9.5 17.2 9.7 17.1C9.9 17 10.1 16.8 10.3 16.6C10.5 16.4 10.5 16.1 10.5 15.8C10.5 15.3 10.4 15 10.1 14.7C9.80001 14.4 9.50001 14.3 9.10001 14.3C9.00001 14.3 8.9 14.3 8.7 14.3C8.5 14.3 8.39999 14.3 8.39999 14.3C8.19999 14.3 7.99999 14.2 7.89999 14.1C7.79999 14 7.7 13.8 7.7 13.7C7.7 13.5 7.79999 13.4 7.89999 13.2C7.99999 13 8.2 13 8.5 13H8.8V13.1ZM15.3 17.5V12.2C14.3 13 13.6 13.3 13.3 13.3C13.1 13.3 13 13.2 12.9 13.1C12.8 13 12.7 12.8 12.7 12.6C12.7 12.4 12.8 12.3 12.9 12.2C13 12.1 13.2 12 13.6 11.8C14.1 11.6 14.5 11.3 14.7 11.1C14.9 10.9 15.2 10.6 15.5 10.3C15.8 10 15.9 9.80003 15.9 9.70003C15.9 9.60003 16.1 9.60004 16.3 9.60004C16.5 9.60004 16.7 9.70003 16.8 9.80003C16.9 9.90003 17 10.2 17 10.5V17.2C17 18 16.7 18.4 16.2 18.4C16 18.4 15.8 18.3 15.6 18.2C15.4 18.1 15.3 17.8 15.3 17.5Z" fill="currentColor"></path>
                  </svg>
                </span>
              </div>
            </div>
          </div>
          <div className="g-2 g-xl-3 mb-10">
            <div className="card card-flush">
              <div className="card-header pt-2">
                <h3 className="card-title align-items-start flex-column">
                  <span className="card-label fw-bold text-dark">{lang.labels.salesVsPurchases}</span>
                  <span className="text-gray-400 pt-1 fw-semibold fs-7">{lang.labels.representationOfSalesAndPurchases}</span>
                </h3>
              </div>
              <div className="card-body py-0 px-5">
                <AppChartBarSalesPurchases id={"chart-bar-purchases-invoises"} companyForUser={companyForUser} info={dashboardComparsion.amounts}></AppChartBarSalesPurchases>
              </div>
            </div>
          </div>
          <div className="d-flex flex-wrap flex-stack mb-5 px-2">
            <h3 className="fw-bold">
              {lang.labels.retailAnalysisSample} 
              <span className="fs-6 text-gray-400 fw-semibold ms-1">{lang.labels.byRecentUpdates}</span>
            </h3>
            <div className="d-flex flex-wrap align-items-center">
              <div className="btn btn-sm btn-icon btn-light btn-color-muted btn-active-primary active">
                <span className="svg-icon svg-icon-1">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 2H10C10.6 2 11 2.4 11 3V10C11 10.6 10.6 11 10 11H3C2.4 11 2 10.6 2 10V3C2 2.4 2.4 2 3 2Z" fill="black"/>
                    <path opacity="0.3" d="M14 2H21C21.6 2 22 2.4 22 3V10C22 10.6 21.6 11 21 11H14C13.4 11 13 10.6 13 10V3C13 2.4 13.4 2 14 2Z" fill="black"/>
                    <path opacity="0.3" d="M3 13H10C10.6 13 11 13.4 11 14V21C11 21.6 10.6 22 10 22H3C2.4 22 2 21.6 2 21V14C2 13.4 2.4 13 3 13Z" fill="black"/>
                    <path opacity="0.3" d="M14 13H21C21.6 13 22 13.4 22 14V21C22 21.6 21.6 22 21 22H14C13.4 22 13 21.6 13 21V14C13 13.4 13.4 13 14 13Z" fill="black"/>
                  </svg>
                </span>
              </div>
            </div>
          </div>
          <div className="row g-2 g-xl-3">
            <div className="col-xl-4 mb-3">
              <div className="card card-flush h-xl-100">
                <div className="card-header pt-2">
                  <h3 className="card-title align-items-start flex-column">
                    <span className="card-label fw-bold text-dark">{lang.labels.quotesToExpire}</span>
                    <span className="text-gray-400 pt-1 fw-semibold fs-7">{dashboardAnalysis.quotes_expire.total} {lang.labels.quotesToExpireThisMonth}</span>
                  </h3>
                  <div className="card-toolbar">
                    <Link to="/app/sale/quote/list" className="btn btn-sm btn-light">{lang.labels.viewAll}</Link>
                  </div>
                </div>
                <div className="card-body pt-0 px-5">
                  <div className="d-flex align-items-end justify-content-end">
                    <span className="text-gray-400 fs-9">{lang.labels.expirationDate}</span>
                  </div>
                  <div className="separator separator-dotted mb-3"></div>
                  <div className="mh-300px hover-scroll-overlay-y">
                    { dashboardAnalysis.quotes_expire.list.map (( (item, index) => { return (
                      <div key={index}>
                        <div className="d-flex flex-stack">
                          <div className="symbol symbol-30px me-3">
                            <div className="symbol-label fs-3 fw-bold text-primary">
                              { item.customer.type === "person" &&
                                item.customer.data.last_name.charAt(0)
                              }
                              { item.customer.type === "company" &&
                                <>
                                  { item.customer.data.logo.length === 0
                                    ?
                                    item.customer.data.name.charAt(0)
                                    :
                                    <img src={imageCompany(item.customer.data.logo)} alt={lang.labels.logo} className="w-20px h-20px" />
                                  }
                                </>
                              }
                            </div>
                          </div>
                          <div className="d-flex align-items-center flex-stack flex-nowrap flex-row-fluid">
                            <div className="row me-5">
                              <span className="text-gray-800 fw-bold">{item.quote_number}</span>
                              <span className="text-gray-400 fw-semibold fs-8">{contactFormat(item.customer)}</span>
                            </div>
                            <div className="d-flex align-items-center">
                              <span className={`badge badge-sm badge-light-${colorValidUntilQuote(item.status, item.valid_until)}`}>{dateFormatAbbreviated(lang.code, item.valid_until)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="separator separator-dashed my-2"></div>
                      </div>
                    )}))}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-4 mb-3">
              <div className="card card-flush h-xl-100">
                <div className="card-header pt-2">
                  <h3 className="card-title align-items-start flex-column">
                    <span className="card-label fw-bold text-dark">{lang.labels.invoicesWithMoreDebt}</span>
                    <span className="text-gray-400 pt-1 fw-semibold fs-7">{dashboardAnalysis.invoices_debt.total} {lang.labels.invoicesNotYetPaid}</span>
                  </h3>
                  <div className="card-toolbar">
                    <Link to="/app/sale/invoice/list" className="btn btn-sm btn-light">{lang.labels.viewAll}</Link>
                  </div>
                </div>
                <div className="card-body pt-0 px-5">
                  <div className="d-flex align-items-end justify-content-end">
                    <span className="text-gray-400 fs-9">{lang.labels.total} ({companyForUser.currency.code}) / {lang.labels.debtPercent}</span>
                  </div>
                  <div className="separator separator-dotted mb-3"></div>
                  <div className="mh-300px hover-scroll-overlay-y">
                    { dashboardAnalysis.invoices_debt.list.map (( (item, index) => { return (
                      <div key={index}>
                        <div className="d-flex flex-stack">
                          <div className="symbol symbol-30px me-3">
                            <div className="symbol-label fs-3 fw-bold text-primary">
                              { item.customer.type === "person" &&
                                item.customer.data.last_name.charAt(0)
                              }
                              { item.customer.type === "company" &&
                                <>
                                  { item.customer.data.logo.length === 0
                                    ?
                                    item.customer.data.name.charAt(0)
                                    :
                                    <img src={imageCompany(item.customer.data.logo)} alt={lang.labels.logo} className="w-20px h-20px" />
                                  }
                                </>
                              }
                            </div>
                          </div>
                          <div className="d-flex align-items-center flex-stack flex-nowrap flex-row-fluid">
                            <div className="row me-5">
                              <span className="text-gray-800 fw-bold">{item.invoice_number}</span>
                              <span className="text-gray-400 fw-semibold fs-8">{contactFormat(item.customer)}</span>
                            </div>
                            <div className="d-flex align-items-center">
                              <span className="text-info fw-bold fs-8 me-2">{moneyFormat(item.amount.total)}</span>
                              <span className={`badge badge-sm badge-light-${colorPercentageDebt(item.amount.total, item.amount.debt)}`}>{percentageCalculate(item.amount.total, item.amount.debt)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="separator separator-dashed my-2"></div>
                      </div>
                    )}))}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-4 mb-3">
              <div className="card card-flush h-xl-100">
                <div className="card-header pt-2">
                  <h3 className="card-title align-items-start flex-column">
                    <span className="card-label fw-bold text-dark">{lang.labels.customerPayments}</span>
                    <span className="text-gray-400 pt-1 fw-semibold fs-7">
                      {topCustomer === "best" ? `${lang.labels.top} ${dashboardAnalysis.customer_payments.best.length} ${lang.labels.bestCustomers}` : `${lang.labels.top} ${dashboardAnalysis.customer_payments.worst.length} ${lang.labels.worstCustomers}`}
                    </span>
                  </h3>
                  <div className="card-toolbar">
                    <ul className="nav" role="tablist">
                      <li className="nav-item" role="presentation" onClick={() => executeChangeTopCustomer("best")}>
                        <a href="#container-customer-best" className="nav-link btn btn-sm btn-color-muted btn-active btn-active-light active fw-bold text-capitalize px-4 me-1" data-bs-toggle="tab" role="tab">{lang.labels.best}</a>
                      </li>
                      <li className="nav-item" role="presentation" onClick={() => executeChangeTopCustomer("worst")}>
                        <a href="#container-customer-worst" className="nav-link btn btn-sm btn-color-muted btn-active btn-active-light fw-bold text-capitalize px-4" data-bs-toggle="tab" role="tab">{lang.labels.worst}</a>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="card-body pt-0 px-5">
                  <div className="d-flex align-items-end justify-content-end">
                    <span className="text-gray-400 fs-9">{lang.labels.total} ({companyForUser.currency.code}) / {lang.labels.paidPercent}</span>
                  </div>
                  <div className="separator separator-dotted mb-3"></div>
                  <div className="tab-content">
                    <div id="container-customer-best" className="tab-pane fade show active" role="tabpanel">
                      <div className="mh-300px hover-scroll-overlay-y">
                        { dashboardAnalysis.customer_payments.best.map (( (item, index) => { return (
                          <div key={index}>
                            <div className="d-flex flex-stack">
                              <div className="symbol symbol-30px me-3">
                                <div className="symbol-label fs-3 fw-bold text-primary">
                                  { item.customer.type === "person" &&
                                    item.customer.data.last_name.charAt(0)
                                  }
                                  { item.customer.type === "company" &&
                                    <>
                                      { item.customer.data.logo.length === 0
                                        ?
                                        item.customer.data.name.charAt(0)
                                        :
                                        <img src={imageCompany(item.customer.data.logo)} alt={lang.labels.logo} className="w-20px h-20px" />
                                      }
                                    </>
                                  }
                                </div>
                              </div>
                              <div className="d-flex align-items-center flex-stack flex-nowrap flex-row-fluid">
                                <div className="row me-5">
                                  <span className="text-gray-800 fw-bold">{contactFormat(item.customer)}</span>
                                  <span className="text-gray-400 fw-semibold fs-8">{item.customer.data.address.city}, {countryName(lang.code, item.customer.data.address.country)}</span>
                                </div>
                                <div className="d-flex align-items-center">
                                  <span className="text-info fw-bold fs-8 me-2">{moneyFormat(item.amount.total_debt)}</span>
                                  <span className="badge badge-sm badge-light-success">{percentageFormat(item.amount.percentage_paid)}</span>
                                </div>
                              </div>
                            </div>
                            <div className="separator separator-dashed my-2"></div>
                          </div>
                        )}))}
                      </div>
                    </div>
                    <div id="container-customer-worst" className="tab-pane fade" role="tabpanel">
                      <div className="mh-300px hover-scroll-overlay-y">
                        { dashboardAnalysis.customer_payments.worst.map (( (item, index) => { return (
                          <div key={index}>
                            <div className="d-flex flex-stack">
                              <div className="symbol symbol-30px me-3">
                                <div className="symbol-label fs-3 fw-bold text-primary">
                                  { item.customer.type === "person" &&
                                    item.customer.data.last_name.charAt(0)
                                  }
                                  { item.customer.type === "company" &&
                                    <>
                                      { item.customer.data.logo.length === 0
                                        ?
                                        item.customer.data.name.charAt(0)
                                        :
                                        <img src={imageCompany(item.customer.data.logo)} alt={lang.labels.logo} className="w-20px h-20px" />
                                      }
                                    </>
                                  }
                                </div>
                              </div>
                              <div className="d-flex align-items-center flex-stack flex-nowrap flex-row-fluid">
                                <div className="row me-5">
                                  <span className="text-gray-800 fw-bold">{contactFormat(item.customer)}</span>
                                  <span className="text-gray-400 fw-semibold fs-8">{item.customer.data.address.city}, {countryName(lang.code, item.customer.data.address.country)}</span>
                                </div>
                                <div className="d-flex align-items-center">
                                  <span className="text-info fw-bold fs-8 me-2">{moneyFormat(item.amount.total_debt)}</span>
                                  <span className="badge badge-sm badge-light-danger">{percentageFormat(item.amount.percentage_paid)}</span>
                                </div>
                              </div>
                            </div>
                            <div className="separator separator-dashed my-2"></div>
                          </div>
                        )}))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default DashboardPage;
